import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, Bell, LogOut } from 'lucide-react'
import { useAuth } from '../../core/auth'
import { useUnreadCounts } from '../contexts/UnreadCountsContext'
import { useActiveModule, MODULE_DEFINITIONS } from '../contexts/ModuleContext'

const routeNames: Record<string, string> = {
  '/lng-ops': 'Dashboard',
  '/lng-ops/live-view': 'Live View',
  '/lng-ops/itinerary': 'Itinerary',
  '/lng-ops/vessels': 'Vessels',
  '/lng-ops/voyage-performance': 'Voyage Performance',
  '/lng-ops/bog-management': 'BOG Management',
  '/lng-ops/vessel-reports': 'Vessel Reports',
  '/lng-ops/graphs': 'Graphs',
  '/lng-ops/reports': 'Reports',
  '/lng-ops/scheduler': 'Scheduler',
  '/lng-ops/admin': 'Admin',
}

interface TopBarProps {
  onOpenCommandPalette: () => void
}

export default function TopBar({ onOpenCommandPalette }: TopBarProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [showMenu, setShowMenu] = useState(false)
  const activeModule = useActiveModule()
  const moduleDefinition = MODULE_DEFINITIONS.find((m) => m.id === activeModule)
  const currentPageName = routeNames[location.pathname] || ''
  const { notifications: notifUnread } = useUnreadCounts()

  return (
    <header className="flex h-12 items-center justify-between bg-bg-secondary border-b border-border-subtle px-4 pl-12 lg:pl-4">
      {/* Left: Page context — primary visual anchor */}
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xs text-text-muted hidden lg:inline">
          {moduleDefinition?.label ?? 'LNG Performance'}
        </span>
        {currentPageName && (
          <>
            <span className="text-text-disabled hidden lg:inline">/</span>
            <h1 className="text-base font-semibold text-text-primary truncate">
              {currentPageName}
            </h1>
          </>
        )}
        {!currentPageName && (
          <h1 className="text-base font-semibold text-text-primary">
            {moduleDefinition?.label ?? 'LNG Performance'}
          </h1>
        )}
        <span className="rounded bg-live-muted px-1.5 py-0.5 text-2xs font-medium text-live">
          Live
        </span>
      </div>

      {/* Center: Search — compact utility, not the visual center */}
      <div className="hidden sm:flex max-w-[240px] flex-shrink-0 items-center mx-4">
        <button
          onClick={onOpenCommandPalette}
          className="flex w-full items-center gap-2 rounded-lg bg-bg-tertiary py-1.5 pl-3 pr-2 text-xs text-text-muted transition-colors hover:bg-bg-hover"
        >
          <Search className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1 text-left">Search</span>
          <kbd className="inline-flex items-center gap-0.5 rounded bg-bg-primary px-1.5 py-0.5 text-2xs font-mono">
            Ctrl+K
          </kbd>
        </button>
      </div>

      {/* Right: User */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/lng-ops/scheduler')}
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-bg-hover hover:text-text-secondary"
          title="Notifications"
        >
          <Bell className="h-4 w-4" />
          {notifUnread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 rounded-full bg-accent flex items-center justify-center text-[9px] font-bold text-accent-fg px-1">
              {notifUnread > 9 ? '9+' : notifUnread}
            </span>
          )}
        </button>

        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-text-muted transition-colors hover:bg-bg-hover hover:text-text-secondary"
          >
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-bg-hi text-2xs font-semibold text-text-primary">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="text-xs text-text-secondary">{user?.name || 'User'}</span>
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full mt-1 z-50 w-48 rounded-lg bg-bg-tertiary shadow-lg">
                <div className="border-b border-border/10 px-3 py-2">
                  <p className="text-xs font-medium text-text-primary">{user?.name}</p>
                  <p className="text-2xs text-text-muted">{user?.email}</p>
                  <p className="text-2xs text-text-muted capitalize">{user?.role}</p>
                </div>
                <button
                  onClick={() => { setShowMenu(false); logout() }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-bg-hover hover:text-negative transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
